(function () {
  const STORAGE_KEY = 'wishlist-items';
  const WISHLIST_HASH = '#wishlist';

  class WishlistApp {
    constructor() {
      this.mainContent = document.getElementById('main-content');
      this.settings = window.theme?.settings || {};
      this.viewStrings = window.theme?.strings?.wishlistView || {};
      this.host = null;
      this.alert = null;
      this.alertTimer = null;
      this.productCache = new Map();
      this.renderToken = 0;

      if (!this.mainContent || !window.localStorage) {
        return;
      }

      this.injectWishlistStylesheet();
      this.host = this.ensureHost();

      if (!this.host) {
        return;
      }

      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleRouteChange = this.handleRouteChange.bind(this);
      this.handleWishlistUpdate = this.handleWishlistUpdate.bind(this);

      document.addEventListener('click', this.handleDocumentClick);
      document.addEventListener('product:variant-change', this.handleVariantChange);
      document.addEventListener('wishlist:updated', this.handleWishlistUpdate);
      window.addEventListener('popstate', this.handleRouteChange);
      window.addEventListener('hashchange', this.handleRouteChange);

      this.observeDynamicContent();
      this.syncHeader();
      this.syncWishlistButtons();
      this.syncWishlistMode();
    }

    observeDynamicContent() {
      const observer = new MutationObserver(() => {
        window.clearTimeout(this.mutationTimer);
        this.mutationTimer = window.setTimeout(() => {
          this.syncWishlistButtons();
        }, 60);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    injectWishlistStylesheet() {
      this.injectStylesheet(this.settings.wishlist_css_url);
    }

    injectCartStylesheet() {
      this.injectStylesheet(this.settings.cart_css_url);
    }

    injectStylesheet(href) {
      if (!href) {
        return;
      }

      const existing = document.querySelector(`link[data-wishlist-stylesheet][href="${href}"]`);
      if (existing) {
        return;
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.wishlistStylesheet = 'true';
      document.head.appendChild(link);
    }

    ensureHost() {
      let host = document.getElementById('wishlist-host');

      if (!host) {
        host = document.createElement('div');
        host.id = 'wishlist-host';
        host.className = 'wishlist-host cart-section-v2';
        host.hidden = true;
        this.mainContent.appendChild(host);
      }

      host.dataset.shopUrl = this.settings.wishlist_shop_url || this.withRoot('');
      return host;
    }

    getStorageItems() {
      try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]');

        if (!Array.isArray(parsed)) {
          return [];
        }

        return parsed
          .map((item) => this.normalizeItem(item))
          .filter((item) => item.productHandle);
      } catch (error) {
        return [];
      }
    }

    normalizeItem(item) {
      const variantId = item && item.variantId !== null && item.variantId !== undefined && item.variantId !== ''
        ? Number(item.variantId)
        : null;

      return {
        productHandle: item?.productHandle ? String(item.productHandle) : '',
        variantId: Number.isFinite(variantId) ? variantId : null,
        addedAt: item?.addedAt || new Date().toISOString()
      };
    }

    setStorageItems(items) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
      document.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: {
          count: items.length
        }
      }));
    }

    getItemKey(item) {
      return item.variantId ? `variant:${item.variantId}` : `product:${item.productHandle}`;
    }

    isItemSaved(productHandle, variantId) {
      if (!productHandle) {
        return false;
      }

      const key = this.getItemKey({
        productHandle,
        variantId: variantId ?? null
      });

      return this.getStorageItems().some((item) => this.getItemKey(item) === key);
    }

    toggleItem(productHandle, variantId) {
      if (!productHandle) {
        return null;
      }

      const normalized = this.normalizeItem({
        productHandle,
        variantId,
        addedAt: new Date().toISOString()
      });
      const key = this.getItemKey(normalized);
      const items = this.getStorageItems();
      const existingIndex = items.findIndex((item) => this.getItemKey(item) === key);
      const isSaved = existingIndex === -1;

      if (existingIndex > -1) {
        items.splice(existingIndex, 1);
      } else {
        items.unshift(normalized);
      }

      this.setStorageItems(items);

      return isSaved;
    }

    removeItemByKey(key) {
      const items = this.getStorageItems().filter((item) => this.getItemKey(item) !== key);
      this.setStorageItems(items);
    }

    clearItems() {
      this.setStorageItems([]);
    }

    getCurrentVariantId(button) {
      if (button.dataset.wishlistUseCurrentVariant !== 'true') {
        const directVariantId = button.dataset.variantId ? Number(button.dataset.variantId) : null;
        return Number.isFinite(directVariantId) ? directVariantId : null;
      }

      const variantId = button.dataset.variantId ? Number(button.dataset.variantId) : null;
      return Number.isFinite(variantId) ? variantId : null;
    }

    getButtonState(button) {
      const productHandle = button.dataset.productHandle;
      const variantId = this.getCurrentVariantId(button);

      return {
        productHandle,
        variantId,
        saved: this.isItemSaved(productHandle, variantId)
      };
    }

    syncHeader() {
      const count = this.getStorageItems().length;

      document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
        badge.textContent = count > 99 ? '*' : String(count);
        badge.hidden = count < 1;
      });

      document.querySelectorAll('[data-wishlist-toggle]').forEach((toggle) => {
        const isActive = this.isWishlistMode();
        toggle.classList.toggle('is-active', isActive);
        toggle.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    syncWishlistButtons() {
      document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
        const state = this.getButtonState(button);

        button.classList.toggle('is-active', state.saved);
        button.setAttribute('aria-pressed', state.saved ? 'true' : 'false');
      });
    }

    handleWishlistUpdate() {
      this.syncHeader();
      this.syncWishlistButtons();

      if (this.isWishlistMode()) {
        this.renderWishlist();
      }
    }

    handleVariantChange(event) {
      const sectionId = event.detail?.sectionId;

      if (sectionId) {
        document
          .querySelectorAll(`[data-wishlist-button][data-wishlist-use-current-variant="true"]`)
          .forEach((button) => {
            const scope = button.closest('.shopify-section');

            if (scope && scope.id === `shopify-section-${sectionId}`) {
              const variantId = event.detail?.variant?.id;
              button.dataset.variantId = variantId ? String(variantId) : '';
            }
          });
      }

      this.syncWishlistButtons();
    }

    handleRouteChange() {
      this.syncWishlistMode();
    }

    handleDocumentClick(event) {
      const toggle = event.target.closest('[data-wishlist-toggle]');
      if (toggle) {
        event.preventDefault();
        this.pushWishlistState(!this.isWishlistMode());
        this.scrollPageToTop();
        return;
      }

      const wishlistButton = event.target.closest('[data-wishlist-button]');
      if (wishlistButton) {
        event.preventDefault();
        const { productHandle, variantId } = this.getButtonState(wishlistButton);
        const isSaved = this.toggleItem(productHandle, variantId);

        if (isSaved !== null) {
          this.showAlert(this.t(isSaved ? 'alert_added' : 'alert_removed'));
        }

        return;
      }

      const removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        event.preventDefault();
        this.removeItemByKey(removeButton.dataset.wishlistRemove);
        return;
      }

      const clearButton = event.target.closest('[data-wishlist-clear]');
      if (clearButton) {
        event.preventDefault();
        this.clearItems();
        return;
      }

      const addAllButton = event.target.closest('[data-wishlist-add-all]');
      if (addAllButton) {
        event.preventDefault();
        this.addAllToCart(addAllButton);
        return;
      }

      const addToCartButton = event.target.closest('[data-wishlist-add-to-cart]');
      if (addToCartButton) {
        event.preventDefault();
        const variantId = Number(addToCartButton.dataset.wishlistAddToCart);

        if (Number.isFinite(variantId)) {
          this.addVariantToCart(variantId, addToCartButton);
        }
      }
    }

    pushWishlistState(isActive) {
      const nextUrl = `${window.location.pathname}${window.location.search}${isActive ? WISHLIST_HASH : ''}`;
      window.history.pushState({}, '', nextUrl);
      this.syncWishlistMode();
    }

    scrollPageToTop() {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto'
      });
    }

    isWishlistMode() {
      return window.location.hash === WISHLIST_HASH;
    }

    syncWishlistMode() {
      if (this.isWishlistMode()) {
        this.openWishlistMode();
      } else {
        this.closeWishlistMode();
      }

      this.syncHeader();
    }

    openWishlistMode() {
      this.injectCartStylesheet();

      Array.from(this.mainContent.children).forEach((child) => {
        if (child === this.host) {
          return;
        }

        if (!child.hasAttribute('data-wishlist-original-hidden')) {
          child.setAttribute('data-wishlist-original-hidden', child.hidden ? 'true' : 'false');
        }

        child.hidden = true;
      });

      this.host.hidden = false;
      this.renderWishlist();
    }

    closeWishlistMode() {
      Array.from(this.mainContent.children).forEach((child) => {
        if (child === this.host) {
          child.hidden = true;
          return;
        }

        const originalHidden = child.getAttribute('data-wishlist-original-hidden');
        if (originalHidden !== null) {
          child.hidden = originalHidden === 'true';
        }
      });
    }

    async renderWishlist() {
      const items = this.getStorageItems();
      const renderToken = ++this.renderToken;

      if (!items.length) {
        this.host.innerHTML = this.renderEmptyState();
        return;
      }

      this.host.innerHTML = this.renderLoadingState();

      try {
        const rows = await this.resolveWishlistRows(items);

        if (renderToken !== this.renderToken) {
          return;
        }

        this.host.innerHTML = this.renderRows(rows);
      } catch (error) {
        if (renderToken !== this.renderToken) {
          return;
        }

        this.host.innerHTML = this.renderErrorState();
      }
    }

    async resolveWishlistRows(items) {
      const handles = [...new Set(items.map((item) => item.productHandle))];
      const products = await Promise.all(handles.map((handle) => this.fetchProduct(handle)));
      const productMap = new Map(handles.map((handle, index) => [handle, products[index]]));

      return items.map((item) => this.mapWishlistRow(item, productMap.get(item.productHandle)));
    }

    async fetchProduct(handle) {
      if (!this.productCache.has(handle)) {
        this.productCache.set(handle, fetch(this.withRoot(`products/${handle}.js`))
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to load product ${handle}`);
            }

            return response.json();
          })
          .catch(() => null));
      }

      return this.productCache.get(handle);
    }

    withRoot(path) {
      const root = window.theme?.routes?.root_url || '/';
      return `${root.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;
    }

    mapWishlistRow(item, product) {
      if (!product) {
        return {
          key: this.getItemKey(item),
          item,
          product: null,
          variant: null,
          resolvedVariant: false,
          productUrl: this.withRoot(`products/${item.productHandle}`),
          imageUrl: '',
          title: item.productHandle,
          optionsText: '',
          price: null,
          compareAtPrice: null,
          available: false
        };
      }

      const variant = item.variantId
        ? product.variants.find((candidate) => Number(candidate.id) === Number(item.variantId)) || null
        : null;
      const resolvedVariant = Boolean(variant);
      const imageUrl = variant?.featured_image?.src || product.featured_image || product.images?.[0] || '';
      // const title = (product.title || item.productHandle).split('|')[0].trim();
      const title = (product.title).trim();

      return {
        key: this.getItemKey(item),
        item,
        product,
        variant,
        resolvedVariant,
        productUrl: this.withRoot(`products/${product.handle}`),
        imageUrl,
        title,
        optionsText: this.getOptionsText(product, variant),
        price: resolvedVariant ? variant.price : product.price,
        compareAtPrice: resolvedVariant ? variant.compare_at_price : product.compare_at_price,
        available: resolvedVariant ? variant.available !== false : true
      };
    }

    getOptionsText(product, variant) {
      if (!variant || !Array.isArray(product?.options) || variant.title === 'Default Title') {
        return '';
      }

      return product.options
        .map((name, index) => {
          const optionName = typeof name === 'string' ? name : name?.name;
          const value = variant.options?.[index];
          return optionName && value ? `${optionName}: ${value}` : '';
        })
        .filter(Boolean)
        .join(' / ');
    }

    formatMoney(value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }

      if (typeof window.formatMoney === 'function') {
        return window.formatMoney(value, window.theme?.settings?.money_with_currency_format || '${{amount}}');
      }

      return String(value);
    }

    renderPrice(price, compareAtPrice) {
      if (price === null || price === undefined) {
        return '';
      }

      if (compareAtPrice && Number(compareAtPrice) > Number(price)) {
        return `<span class="price"><del><span class="amount">${this.formatMoney(compareAtPrice)}</span></del><ins><span class="amount">${this.formatMoney(price)}</span></ins></span>`;
      }

      return `<span class="price"><ins><span class="amount">${this.formatMoney(price)}</span></ins></span>`;
    }

    t(key, replacements = {}) {
      const value = key.split('.').reduce((current, part) => {
        if (!current || typeof current !== 'object') {
          return undefined;
        }

        return current[part];
      }, this.viewStrings);

      if (value === undefined || value === null || typeof value === 'object') {
        return '';
      }

      return Object.entries(replacements).reduce((text, [name, replacement]) => {
        return text.replace(new RegExp(`{{\\s*${name}\\s*}}`, 'g'), () => String(replacement));
      }, String(value));
    }

    getReturnUrl() {
      return `${window.location.pathname}${window.location.search}`;
    }

    ensureAlert() {
      if (this.alert) {
        return this.alert;
      }

      this.alert = document.createElement('div');
      this.alert.className = 'wishlist-alert';
      this.alert.setAttribute('role', 'status');
      this.alert.setAttribute('aria-live', 'polite');
      document.body.appendChild(this.alert);

      return this.alert;
    }

    showAlert(message) {
      if (!message) {
        return;
      }

      const alert = this.ensureAlert();
      window.clearTimeout(this.alertTimer);
      alert.textContent = message;
      alert.classList.add('is-visible');

      this.alertTimer = window.setTimeout(() => {
        alert.classList.remove('is-visible');
      }, 3000);
    }

    renderWishlistView(content, title = this.t('title')) {
      return `
        <div class="wishlist-view">
          <div class="row">
            <div class="small-12 columns">
              <div class="thb-cart-form">
                <div class="thb-cart-form--header">
                  <a href="${this.getReturnUrl()}">
                    ${this.t('icons.arrow_left')}
                    <span>${title}</span>
                  </a>
                </div>
                ${content}
              </div>
            </div>
          </div>
        </div>
      `;
    }

    renderEmptyContainer(content) {
      return this.renderWishlistView(`
        <div class="wishlist-empty">
          ${content}
        </div>
      `);
    }

    renderLoadingState() {
      return this.renderEmptyContainer(`
        <p>${this.t('loading')}</p>
      `);
    }

    renderErrorState() {
      return this.renderEmptyState();
      return this.renderEmptyContainer(`
        <h2>${this.t('unavailable_title')}</h2>
        <p>${this.t('unavailable_text')}</p>
      `);
    }

    renderEmptyState() {
      const shopUrl = this.host.dataset.shopUrl || this.settings.wishlist_shop_url || this.withRoot('');

      return this.renderEmptyContainer(`
        <p><strong>${this.t('empty_title')}</strong></p>
        <p>${this.t('empty_text')}</p>
        <a href="${shopUrl}" class="button"><span>${this.t('start_shopping')}</span></a>
      `);
    }

    renderRows(rows) {
      const count = rows.length;
      const addableRows = rows.filter((row) => row.resolvedVariant && row.available);

      return this.renderWishlistView(`
        <div class="thb-cart-form--container">
          <table class="cart-items no-borders">
            <thead>
              <tr>
                <th class="product-name">${this.t('product')}</th>
                <th class="product-clear"></th>
              </tr>
            </thead>
            <tbody>
              ${rows.map((row) => this.renderRow(row)).join('')}
            </tbody>
          </table>
          <div class="thb-cart-form--footer-container">
            <div class="row">
              <div class="small-12 columns">
                <div class="thb-cart-form--footer wishlist-footer-actions">
                  <div class="thb-cart-form--continue wishlist-footer-continue">
                    <a href="${this.getReturnUrl()}" class="back_to_shop text-button">${this.t('icons.caret_left')} <span>${this.t('continue_shopping')}</span></a>
                  </div>
                  <div class="wishlist-footer-buttons">
                    <button type="button" class="button outline" data-wishlist-clear ${count ? '' : 'disabled'}><span>${this.t('remove_all')}</span></button>
                    <button type="button" class="button full" data-wishlist-add-all ${addableRows.length ? '' : 'disabled'}><span>${this.t('add_all_to_cart')}</span></button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `, this.t('title_with_count', {
        count
      }));
    }

    renderRow(row) {
      const action = row.resolvedVariant && row.available 
        ? `<button type="button" class="button outline" data-wishlist-add-to-cart="${row.variant.id}">${this.t('icons.add_to_cart')} <span>${this.t('add_to_cart')}</span></button>`
        : '';
      const removeAction = `<button type="button" class="remove" data-wishlist-remove="${this.escapeHtml(row.key)}" aria-label="${this.escapeHtml(this.t('remove_item_for', {
          product_title: row.title
        }))}">${this.t('icons.close')} <span>${this.t('remove_item')}</span></button>`;
      const optionsMarkup = row.optionsText
        ? `<div class="product-cart-item-options">${row.optionsText}</div>`
        : '';
      const imageMarkup = row.imageUrl
        ? `<a href="${row.productUrl}"><img src="${row.imageUrl}" alt="${this.escapeHtml(row.title)}" loading="lazy"></a>`
        : `<div class="thb-placeholder" style="width: 100px;">&nbsp;</div>`;

      return `
        <tr id="WishlistItem-${this.escapeHtml(row.key)}">
          <td class="product-name">
            <div class="product-cart-item">
              <div class="product-cart-item-image">
                ${imageMarkup}
              </div>
              <div class="product-cart-item-info">
                <a href="${row.productUrl}" class="cart-product-link">${this.escapeHtml(row.title)}</a>
                ${optionsMarkup}
                <div class="product-subtotal">
                  ${this.renderPrice(row.price, row.compareAtPrice)}
                </div>
                ${removeAction}
              </div>
            </div>
          </td>
          <td class="product-clear">
            <div class="wishlist-item-actions">
              ${removeAction}
              ${action}
            </div>
          </td>
        </tr>
      `;
    }

    async addVariantToCart(variantId, button) {
      this.setLoading(button, true);

      try {
        const response = await fetch(`${window.theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: variantId,
              quantity: 1
            }]
          })
        });

        if (!response.ok) {
          throw new Error('Cart add failed');
        }

        this.refreshCartUI();
      } finally {
        this.setLoading(button, false);
      }
    }

    async addAllToCart(button) {
      const rows = await this.resolveWishlistRows(this.getStorageItems());
      const items = rows
        .filter((row) => row.resolvedVariant && row.available)
        .map((row) => ({
          id: row.variant.id,
          quantity: 1
        }));

      if (!items.length) {
        return;
      }

      this.setLoading(button, true);

      try {
        const response = await fetch(`${window.theme.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items
          })
        });

        if (!response.ok) {
          throw new Error('Bulk cart add failed');
        }

        this.refreshCartUI();
      } finally {
        this.setLoading(button, false);
      }
    }

    refreshCartUI() {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    setLoading(button, isLoading) {
      if (!button) {
        return;
      }

      button.disabled = isLoading;
      button.classList.toggle('loading', isLoading);
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    getBackIcon() {
      return `
        <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 1L1 5L5 9" stroke="var(--color-body, #151515)" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }

    getCloseIcon() {
      return `
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 1L1 13M13 13L1 1" stroke="var(--color-body)" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    window.themeWishlist = new WishlistApp();
  });
}());
